import { ref, watch, nextTick, onMounted, onUnmounted, type Ref } from 'vue';
import type { Swiper as SwiperClass } from 'swiper/types';

// ===========================
// CONFIGURATION CONSTANTS
// ===========================

const ZOOM_CONFIG = {
    MIN_LEVEL: 1,
    MAX_LEVEL: 4,
    STEP_SIZE: 0.5,
    DEFAULT_ZOOM_LEVEL: 2, // For double-click/tap zoom
    // Wheel zoom configuration for smoother Magic Mouse experience
    WHEEL_ZOOM_SENSITIVITY: 0.005, // How sensitive wheel zoom is to delta values
    MIN_WHEEL_STEP: 0.1, // Minimum zoom step for wheel events
    MAX_WHEEL_STEP: 0.3, // Maximum zoom step for wheel events
} as const;

const DOUBLE_TAP_CONFIG = {
    MAX_DELAY_MS: 300, // Maximum time between taps to be considered a double-tap
    MAX_DISTANCE_PX: 50, // Maximum distance between taps
} as const;

const PAN_CONSTRAINTS = {
    // More generous multiplier for better panning experience
    MAX_PAN_MULTIPLIER: 300, // Multiplied by (zoomLevel - 1) for dynamic boundaries
    // Alternative: calculate based on container dimensions
    USE_CONTAINER_BASED_CONSTRAINTS: true,
} as const;

// ===========================
// TYPES
// ===========================

interface SlideZoomState {
    zoomLevel: number;
    panOffsetX: number;
    panOffsetY: number;
}

interface LightboxZoomComposable {
    // Reactive state
    currentZoomLevel: Ref<number>;
    isImageZoomed: Ref<boolean>;
    isUserPanning: Ref<boolean>;
    currentPanOffset: Ref<{ x: number; y: number }>;
    isRenderingInProgress: Ref<boolean>;

    // Swiper refs
    mainSwiper: Ref<SwiperClass | null>;
    thumbsSwiper: Ref<SwiperClass | null>;

    // Core functions
    initializeMainSwiper: (swiper: SwiperClass) => void;
    initializeThumbnailSwiper: (swiper: SwiperClass) => void;
    resetCurrentSlideZoom: () => void;
    resetAllZoomStates: () => void;
    increaseZoomLevel: () => void;
    decreaseZoomLevel: () => void;
    getImageTransformStyle: (imageIndex: number) => Record<string, string>;

    // Event handlers
    handleDoubleClick: (event: MouseEvent) => void;
    handleTouchStart: (event: TouchEvent) => void;
    handleTouchMove: (event: TouchEvent) => void;
    handleTouchEnd: (event: TouchEvent) => void;
    handleMouseDown: (event: MouseEvent) => void;
    handleMouseMove: (event: MouseEvent) => void;
    handleMouseUp: () => void;
    handleWheel: (event: WheelEvent) => void;
    handleKeydown: (event: KeyboardEvent) => void;
    handleImageLoad: () => void;

    // Slide management
    onLightboxSlideChange: () => void;
    navigateToSlide: (slideIndex: number, speed?: number) => void;
    restoreSlideZoomState: (slideIndex: number) => void;

    // Browser state management
    preventBrowserZoom: () => void;
    restoreBrowserZoom: () => void;
    preventPageScrolling: () => void;
    restorePageScrolling: () => void;
}

export function useLightboxZoom(): LightboxZoomComposable {
    // ===========================
    // REACTIVE STATE MANAGEMENT
    // ===========================

    // Zoom state management
    const currentZoomLevel = ref<number>(ZOOM_CONFIG.MIN_LEVEL);
    const isImageZoomed = ref(false);

    // Pan state management
    const isUserPanning = ref(false);
    const panningStartPosition = ref({ x: 0, y: 0 });
    const currentPanOffset = ref({ x: 0, y: 0 });
    const initialPanOffset = ref({ x: 0, y: 0 });

    // Touch interaction state
    const activeTouches = ref<Touch[]>([]);
    const lastMeasuredTouchDistance = ref(0);
    const baselineZoomLevel = ref(1);

    // Double-tap detection state
    const lastTapTimestamp = ref(0);
    const lastTapCoordinates = ref({ x: 0, y: 0 });

    // Performance optimization state
    const isRenderingInProgress = ref(false);
    let pendingAnimationFrame: number | null = null;

    // Per-slide zoom state persistence
    const slideZoomStates = ref<Record<number, SlideZoomState>>({});

    // Swiper instances
    const mainSwiper = ref<SwiperClass | null>(null);
    const thumbsSwiper = ref<SwiperClass | null>(null);

    // Browser state backup for restoration
    let previousBodyOverflow = '';
    let previousBodyPosition = '';
    let previousBodyWidth = '';
    let scrollPosition = 0;
    let originalViewport: HTMLMetaElement | null = null;

    // ===========================
    // CORE COMPONENT FUNCTIONS
    // ===========================

    /**
     * Initialize main image swiper instance
     */
    const initializeMainSwiper = (swiper: SwiperClass): void => {
        mainSwiper.value = swiper;
    };

    /**
     * Initialize thumbnail swiper instance
     */
    const initializeThumbnailSwiper = (swiper: SwiperClass): void => {
        thumbsSwiper.value = swiper;
    };

    // ===========================
    // ZOOM CONTROL FUNCTIONS
    // ===========================

    /**
     * Resets zoom level and pan position for the current slide
     */
    const resetCurrentSlideZoom = (): void => {
        const currentSlideIndex = mainSwiper.value?.activeIndex ?? 0;

        currentZoomLevel.value = ZOOM_CONFIG.MIN_LEVEL as number;
        currentPanOffset.value = { x: 0, y: 0 };
        isImageZoomed.value = false;
        isUserPanning.value = false;

        // Re-enable Swiper navigation when zoom is reset
        enableSwiperNavigation();

        // Clear persisted state for current slide
        delete slideZoomStates.value[currentSlideIndex];
    };

    /**
     * Resets zoom and pan for all slides and clears all stored states
     */
    const resetAllZoomStates = (): void => {
        currentZoomLevel.value = ZOOM_CONFIG.MIN_LEVEL as number;
        currentPanOffset.value = { x: 0, y: 0 };
        isImageZoomed.value = false;
        isUserPanning.value = false;
        slideZoomStates.value = {};

        enableSwiperNavigation();
    };

    /**
     * Increases zoom level by one step
     */
    const increaseZoomLevel = (): void => {
        const newZoomLevel = Math.min(currentZoomLevel.value + ZOOM_CONFIG.STEP_SIZE, ZOOM_CONFIG.MAX_LEVEL);
        applyZoomLevel(newZoomLevel);
    };

    /**
     * Decreases zoom level by one step
     */
    const decreaseZoomLevel = (): void => {
        const newZoomLevel = Math.max(currentZoomLevel.value - ZOOM_CONFIG.STEP_SIZE, ZOOM_CONFIG.MIN_LEVEL);
        applyZoomLevel(newZoomLevel);
    };

    /**
     * Applies a specific zoom level with optional center point for zooming
     * @param targetZoomLevel - The desired zoom level
     * @param centerX - Optional X coordinate for zoom center
     * @param centerY - Optional Y coordinate for zoom center
     */
    const applyZoomLevel = (targetZoomLevel: number, centerX?: number, centerY?: number): void => {
        // Reset zoom if targeting minimum level
        if (targetZoomLevel <= ZOOM_CONFIG.MIN_LEVEL) {
            resetCurrentSlideZoom();
            return;
        }

        const previousZoomLevel = currentZoomLevel.value;
        currentZoomLevel.value = targetZoomLevel;
        isImageZoomed.value = targetZoomLevel > ZOOM_CONFIG.MIN_LEVEL;

        // Adjust pan position when zooming with a center point
        if (centerX !== undefined && centerY !== undefined) {
            const zoomRatio = targetZoomLevel / previousZoomLevel;
            currentPanOffset.value = {
                x: centerX - (centerX - currentPanOffset.value.x) * zoomRatio,
                y: centerY - (centerY - currentPanOffset.value.y) * zoomRatio,
            };
        }

        // Keep panning within image boundaries
        constrainPanningToBounds();

        // Disable Swiper navigation when zoomed in
        if (isImageZoomed.value) {
            disableSwiperNavigation();
        }

        // Persist zoom state for current slide
        saveCurrentSlideZoomState();
    };

    /**
     * Constrains panning to stay within reasonable image bounds
     */
    const constrainPanningToBounds = (): void => {
        if (currentZoomLevel.value <= ZOOM_CONFIG.MIN_LEVEL) return;

        let maxPanX: number, maxPanY: number;

        if (PAN_CONSTRAINTS.USE_CONTAINER_BASED_CONSTRAINTS) {
            // Calculate constraints based on how much the image extends beyond the container
            const scaleFactor = currentZoomLevel.value;
            const extraWidth = (scaleFactor - 1) * (window.innerWidth / 2);
            const extraHeight = (scaleFactor - 1) * (window.innerHeight / 2);

            // Allow panning to show the parts of the image that extend beyond the viewport
            maxPanX = Math.max(extraWidth, PAN_CONSTRAINTS.MAX_PAN_MULTIPLIER);
            maxPanY = Math.max(extraHeight, PAN_CONSTRAINTS.MAX_PAN_MULTIPLIER);
        } else {
            // Use the original fixed multiplier method
            const maxPanDistance = (currentZoomLevel.value - 1) * PAN_CONSTRAINTS.MAX_PAN_MULTIPLIER;
            maxPanX = maxPanDistance;
            maxPanY = maxPanDistance;
        }

        currentPanOffset.value = {
            x: Math.max(-maxPanX, Math.min(maxPanX, currentPanOffset.value.x)),
            y: Math.max(-maxPanY, Math.min(maxPanY, currentPanOffset.value.y)),
        };
    };

    // ===========================
    // SWIPER NAVIGATION CONTROL
    // ===========================

    /**
     * Enables Swiper touch navigation
     */
    const enableSwiperNavigation = (): void => {
        if (mainSwiper.value) {
            mainSwiper.value.allowTouchMove = true;
        }
    };

    /**
     * Disables Swiper touch navigation (used when zoomed)
     */
    const disableSwiperNavigation = (): void => {
        if (mainSwiper.value) {
            mainSwiper.value.allowTouchMove = false;
        }
    };

    /**
     * Generic function to navigate both main and thumbnail swipers to a specific slide
     */
    const navigateToSlide = (slideIndex: number, speed: number = 0): void => {
        if (mainSwiper.value) {
            mainSwiper.value.slideTo(slideIndex, speed);
        }
        if (thumbsSwiper.value) {
            thumbsSwiper.value.slideTo(slideIndex, speed);
        }
    };

    /**
     * Generic function to handle pan movement in a specific direction
     */
    const movePanInDirection = (direction: 'left' | 'right' | 'up' | 'down', amount: number = 50): void => {
        if (!isImageZoomed.value) return;

        switch (direction) {
            case 'left':
                currentPanOffset.value.x += amount;
                break;
            case 'right':
                currentPanOffset.value.x -= amount;
                break;
            case 'up':
                currentPanOffset.value.y += amount;
                break;
            case 'down':
                currentPanOffset.value.y -= amount;
                break;
        }

        constrainPanningToBounds();
    };

    // ===========================
    // STATE PERSISTENCE FUNCTIONS
    // ===========================

    /**
     * Saves current zoom and pan state for the active slide
     */
    const saveCurrentSlideZoomState = (): void => {
        const currentSlideIndex = mainSwiper.value?.activeIndex ?? 0;

        slideZoomStates.value[currentSlideIndex] = {
            zoomLevel: currentZoomLevel.value,
            panOffsetX: currentPanOffset.value.x,
            panOffsetY: currentPanOffset.value.y,
        };
    };

    /**
     * Restores zoom and pan state for a specific slide
     */
    const restoreSlideZoomState = (slideIndex: number): void => {
        const savedState = slideZoomStates.value[slideIndex];

        if (savedState) {
            currentZoomLevel.value = savedState.zoomLevel;
            currentPanOffset.value = {
                x: savedState.panOffsetX,
                y: savedState.panOffsetY,
            };
            isImageZoomed.value = savedState.zoomLevel > ZOOM_CONFIG.MIN_LEVEL;

            // Disable Swiper navigation if zoomed
            if (isImageZoomed.value) {
                disableSwiperNavigation();
            } else {
                enableSwiperNavigation();
            }
        } else {
            resetCurrentSlideZoom();
        }
    };

    // ===========================
    // VIEWPORT AND SCROLL MANAGEMENT
    // ===========================

    /**
     * Manages viewport meta tag to prevent browser zoom
     */
    const preventBrowserZoom = (): void => {
        originalViewport = document.querySelector('meta[name="viewport"]');
        if (originalViewport) {
            const currentContent = originalViewport.getAttribute('content') || '';
            if (!currentContent.includes('user-scalable=no')) {
                originalViewport.setAttribute('content', currentContent + ', user-scalable=no');
            }
        } else {
            // Create viewport meta tag if it doesn't exist
            const viewportMeta = document.createElement('meta');
            viewportMeta.name = 'viewport';
            viewportMeta.content = 'width=device-width, initial-scale=1, user-scalable=no';
            document.head.appendChild(viewportMeta);
            originalViewport = viewportMeta;
        }
    };

    /**
     * Restores original viewport settings
     */
    const restoreBrowserZoom = (): void => {
        if (originalViewport) {
            const currentContent = originalViewport.getAttribute('content') || '';
            if (currentContent.includes(', user-scalable=no')) {
                const restoredContent = currentContent.replace(', user-scalable=no', '');
                if (restoredContent.trim()) {
                    originalViewport.setAttribute('content', restoredContent);
                } else {
                    originalViewport.remove();
                }
            }
            originalViewport = null;
        }
    };

    /**
     * Prevents page scrolling by fixing body position
     */
    const preventPageScrolling = (): void => {
        // Save current body styles and scroll position
        previousBodyOverflow = document.body.style.overflow;
        previousBodyPosition = document.body.style.position;
        previousBodyWidth = document.body.style.width;
        scrollPosition = window.pageYOffset || document.documentElement.scrollTop;

        // Apply fixed positioning to prevent scrolling
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
        document.body.style.top = `-${scrollPosition}px`;
        document.body.classList.add('lightbox-open');
    };

    /**
     * Restores page scrolling and position
     */
    const restorePageScrolling = (): void => {
        // Restore body styles
        document.body.style.overflow = previousBodyOverflow;
        document.body.style.position = previousBodyPosition;
        document.body.style.width = previousBodyWidth;
        document.body.style.top = '';
        document.body.classList.remove('lightbox-open');

        // Restore scroll position
        if (scrollPosition > 0) {
            window.scrollTo(0, scrollPosition);
        }
    };

    // ===========================
    // PERFORMANCE OPTIMIZATION
    // ===========================

    /**
     * Optimized state update using requestAnimationFrame to prevent excessive renders
     */
    const scheduleStateUpdate = (): void => {
        if (pendingAnimationFrame) {
            cancelAnimationFrame(pendingAnimationFrame);
        }

        pendingAnimationFrame = requestAnimationFrame(() => {
            isRenderingInProgress.value = false;
            saveCurrentSlideZoomState();
        });
    };

    /**
     * Generates CSS transform style for image based on current zoom and pan state
     */
    const getImageTransformStyle = (imageIndex: number): Record<string, string> => {
        const activeSlideIndex = mainSwiper.value?.activeIndex ?? 0;

        // Only apply transform to the currently active slide
        if (imageIndex !== activeSlideIndex) {
            return {
                transform: 'translate(0px, 0px) scale(1)',
            };
        }

        return {
            transform: `translate(${currentPanOffset.value.x}px, ${currentPanOffset.value.y}px) scale(${currentZoomLevel.value})`,
            transformOrigin: 'center center',
        };
    };

    // ===========================
    // EVENT HANDLERS
    // ===========================

    const handleDoubleClick = (event: MouseEvent) => {
        event.preventDefault();

        if (isImageZoomed.value) {
            resetCurrentSlideZoom();
        } else {
            const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
            const centerX = event.clientX - rect.left - rect.width / 2;
            const centerY = event.clientY - rect.top - rect.height / 2;
            applyZoomLevel(ZOOM_CONFIG.DEFAULT_ZOOM_LEVEL, centerX, centerY);
        }
    };

    const handleDoubleTap = (x: number, y: number, rect: DOMRect) => {
        if (isImageZoomed.value) {
            resetCurrentSlideZoom();
        } else {
            const centerX = x - rect.left - rect.width / 2;
            const centerY = y - rect.top - rect.height / 2;
            applyZoomLevel(ZOOM_CONFIG.DEFAULT_ZOOM_LEVEL, centerX, centerY);
        }
    };

    const detectDoubleTap = (event: TouchEvent) => {
        const currentTime = Date.now();
        const touch = event.touches[0] || event.changedTouches[0];
        const currentPosition = { x: touch.clientX, y: touch.clientY };

        const timeDiff = currentTime - lastTapTimestamp.value;
        const distanceSquared =
            Math.pow(currentPosition.x - lastTapCoordinates.value.x, 2) +
            Math.pow(currentPosition.y - lastTapCoordinates.value.y, 2);
        const distance = Math.sqrt(distanceSquared);

        let isDoubleTap = false;

        if (timeDiff < DOUBLE_TAP_CONFIG.MAX_DELAY_MS && distance < DOUBLE_TAP_CONFIG.MAX_DISTANCE_PX) {
            isDoubleTap = true;
            const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
            handleDoubleTap(currentPosition.x, currentPosition.y, rect);

            // Reset detection state
            lastTapTimestamp.value = 0;
            lastTapCoordinates.value = { x: 0, y: 0 };
        } else {
            // Update for potential next tap
            lastTapTimestamp.value = currentTime;
            lastTapCoordinates.value = currentPosition;
        }

        return isDoubleTap;
    };

    const handleWheel = (event: WheelEvent) => {
        event.preventDefault();

        // Calculate dynamic zoom step based on wheel delta for better Magic Mouse support
        const baseDelta = Math.abs(event.deltaY);
        const normalizedDelta = Math.min(baseDelta * ZOOM_CONFIG.WHEEL_ZOOM_SENSITIVITY, ZOOM_CONFIG.MAX_WHEEL_STEP);
        const zoomStep = Math.max(normalizedDelta, ZOOM_CONFIG.MIN_WHEEL_STEP);

        const zoomDirection = event.deltaY > 0 ? -zoomStep : zoomStep;
        const newZoom = Math.max(
            ZOOM_CONFIG.MIN_LEVEL,
            Math.min(ZOOM_CONFIG.MAX_LEVEL, currentZoomLevel.value + zoomDirection),
        );

        if (Math.abs(newZoom - currentZoomLevel.value) > 0.01) {
            // Only apply if change is meaningful
            const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
            const centerX = event.clientX - rect.left - rect.width / 2;
            const centerY = event.clientY - rect.top - rect.height / 2;
            applyZoomLevel(newZoom, centerX, centerY);
        }
    };

    const getTouchDistance = (touches: TouchList): number => {
        if (touches.length < 2) return 0;
        const dx = touches[0].clientX - touches[1].clientX;
        const dy = touches[0].clientY - touches[1].clientY;
        return Math.sqrt(dx * dx + dy * dy);
    };

    const getTouchCenter = (touches: TouchList): { x: number; y: number } => {
        if (touches.length < 2) return { x: 0, y: 0 };
        return {
            x: (touches[0].clientX + touches[1].clientX) / 2,
            y: (touches[0].clientY + touches[1].clientY) / 2,
        };
    };

    const handleTouchStart = (event: TouchEvent) => {
        if (pendingAnimationFrame) {
            cancelAnimationFrame(pendingAnimationFrame);
            pendingAnimationFrame = null;
        }

        activeTouches.value = Array.from(event.touches);

        if (event.touches.length === 1) {
            const isDoubleTap = detectDoubleTap(event);

            if (!isDoubleTap && isImageZoomed.value) {
                // Start panning
                isUserPanning.value = true;
                isRenderingInProgress.value = true;

                panningStartPosition.value = {
                    x: event.touches[0].clientX,
                    y: event.touches[0].clientY,
                };
                initialPanOffset.value = { ...currentPanOffset.value };

                disableSwiperNavigation();
            }
        } else if (event.touches.length === 2) {
            // Start zooming
            isUserPanning.value = false;
            isRenderingInProgress.value = true;
            lastMeasuredTouchDistance.value = getTouchDistance(event.touches);
            baselineZoomLevel.value = currentZoomLevel.value;

            disableSwiperNavigation();
            event.preventDefault(); // Prevent browser zoom
        }
    };

    const handleTouchMove = (event: TouchEvent) => {
        if (event.touches.length === 1 && isUserPanning.value && isImageZoomed.value) {
            // Handle panning
            const touch = event.touches[0];
            const deltaX = touch.clientX - panningStartPosition.value.x;
            const deltaY = touch.clientY - panningStartPosition.value.y;

            // Update pan position directly for responsiveness
            currentPanOffset.value.x = initialPanOffset.value.x + deltaX;
            currentPanOffset.value.y = initialPanOffset.value.y + deltaY;

            constrainPanningToBounds();

            // Throttle state updates
            if (!isRenderingInProgress.value) {
                isRenderingInProgress.value = true;
                scheduleStateUpdate();
            }
        } else if (event.touches.length === 2) {
            // Handle pinch-to-zoom
            const currentDistance = getTouchDistance(event.touches);

            if (currentDistance > 0 && lastMeasuredTouchDistance.value > 0) {
                const distanceRatio = currentDistance / lastMeasuredTouchDistance.value;
                const newZoom = Math.max(
                    ZOOM_CONFIG.MIN_LEVEL,
                    Math.min(ZOOM_CONFIG.MAX_LEVEL, baselineZoomLevel.value * distanceRatio),
                );

                const center = getTouchCenter(event.touches);
                const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
                const centerX = center.x - rect.left - rect.width / 2;
                const centerY = center.y - rect.top - rect.height / 2;

                applyZoomLevel(newZoom, centerX, centerY);
                scheduleStateUpdate();
            }

            event.preventDefault(); // Prevent browser zoom
        }
    };

    const handleTouchEnd = (event: TouchEvent) => {
        if (event.touches.length === 0) {
            isUserPanning.value = false;
            isRenderingInProgress.value = false;
            activeTouches.value = [];

            if (pendingAnimationFrame) {
                cancelAnimationFrame(pendingAnimationFrame);
                pendingAnimationFrame = null;
            }

            if (mainSwiper.value && !isImageZoomed.value) {
                enableSwiperNavigation();
            }
        }
    };

    const handleMouseDown = (event: MouseEvent) => {
        if (isImageZoomed.value && event.button === 0) {
            // Only left mouse button
            isUserPanning.value = true;
            isRenderingInProgress.value = true;

            panningStartPosition.value = { x: event.clientX, y: event.clientY };
            initialPanOffset.value = { ...currentPanOffset.value };

            disableSwiperNavigation();
        }
    };

    const handleMouseMove = (event: MouseEvent) => {
        if (isUserPanning.value && isImageZoomed.value) {
            const deltaX = event.clientX - panningStartPosition.value.x;
            const deltaY = event.clientY - panningStartPosition.value.y;

            // Update pan position directly for responsiveness
            currentPanOffset.value.x = initialPanOffset.value.x + deltaX;
            currentPanOffset.value.y = initialPanOffset.value.y + deltaY;

            constrainPanningToBounds();

            // Throttle state updates
            if (!isRenderingInProgress.value) {
                isRenderingInProgress.value = true;
                scheduleStateUpdate();
            }
        }
    };

    const handleMouseUp = () => {
        if (isUserPanning.value) {
            isUserPanning.value = false;
            isRenderingInProgress.value = false;

            if (pendingAnimationFrame) {
                cancelAnimationFrame(pendingAnimationFrame);
                pendingAnimationFrame = null;
            }

            if (mainSwiper.value && !isImageZoomed.value) {
                enableSwiperNavigation();
            }
        }
    };

    const handleKeydown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
            // This should be handled by the parent component
            return;
        } else if (event.key === '+' || event.key === '=') {
            event.preventDefault();
            increaseZoomLevel();
        } else if (event.key === '-') {
            event.preventDefault();
            decreaseZoomLevel();
        } else if (event.key === '0') {
            event.preventDefault();
            resetCurrentSlideZoom();
        } else if (isImageZoomed.value) {
            // Pan controls when zoomed
            switch (event.key) {
                case 'ArrowLeft':
                    event.preventDefault();
                    movePanInDirection('left', 20);
                    break;
                case 'ArrowRight':
                    event.preventDefault();
                    movePanInDirection('right', 20);
                    break;
                case 'ArrowUp':
                    event.preventDefault();
                    movePanInDirection('up', 20);
                    break;
                case 'ArrowDown':
                    event.preventDefault();
                    movePanInDirection('down', 20);
                    break;
            }
        }
    };

    const handleImageLoad = () => {
        // Restore zoom state when new image loads
        const currentSlide = mainSwiper.value?.activeIndex ?? 0;
        restoreSlideZoomState(currentSlide);
    };

    const onLightboxSlideChange = () => {
        if (mainSwiper.value) {
            const currentSlide = mainSwiper.value.activeIndex;
            restoreSlideZoomState(currentSlide);
        }
    };

    // ===========================
    // LIFECYCLE HOOKS
    // ===========================

    onMounted(() => {
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    });

    onUnmounted(() => {
        // Clean up body styles
        document.body.classList.remove('lightbox-open');
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.top = '';

        // Cancel any pending animation frames
        if (pendingAnimationFrame) {
            cancelAnimationFrame(pendingAnimationFrame);
        }

        // Remove global event listeners
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
    });

    // ===========================
    // RETURN PUBLIC API
    // ===========================

    return {
        // Reactive state
        currentZoomLevel,
        isImageZoomed,
        isUserPanning,
        currentPanOffset,
        isRenderingInProgress,

        // Swiper refs
        mainSwiper,
        thumbsSwiper,

        // Core functions
        initializeMainSwiper,
        initializeThumbnailSwiper,
        resetCurrentSlideZoom,
        resetAllZoomStates,
        increaseZoomLevel,
        decreaseZoomLevel,
        getImageTransformStyle,

        // Event handlers
        handleDoubleClick,
        handleTouchStart,
        handleTouchMove,
        handleTouchEnd,
        handleMouseDown,
        handleMouseMove,
        handleMouseUp,
        handleWheel,
        handleKeydown,
        handleImageLoad,

        // Slide management
        onLightboxSlideChange,
        navigateToSlide,
        restoreSlideZoomState,

        // Browser state management
        preventBrowserZoom,
        restoreBrowserZoom,
        preventPageScrolling,
        restorePageScrolling,
    };
}
